import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const RecommendationsTable = ({ recommendations, selectedRec, onSelectRec }) => {
  const { t, i18n } = useTranslation();
  const [expandedRowId, setExpandedRowId] = useState(null);

  const formatCurrency = (amount, currency = 'USD', locale = 'en') => {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(amount);
  };

  const getRiskBadgeStyle = (riskLevel) => {
    const level = (riskLevel || 'Medium').toLowerCase();
    let bg = 'rgba(245, 158, 11, 0.15)'; // Yellow default
    let border = 'rgba(245, 158, 11, 0.4)';
    let color = '#FCD34D';

    if (level === 'low') {
      bg = 'rgba(16, 185, 129, 0.15)'; // Green
      border = 'rgba(16, 185, 129, 0.4)';
      color = '#A7F3D0';
    } else if (level === 'high') {
      bg = 'rgba(239, 68, 68, 0.15)'; // Red
      border = 'rgba(239, 68, 68, 0.4)';
      color = '#FCA5A5';
    }

    return {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '3px 10px',
      borderRadius: '999px',
      fontSize: '0.75rem',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.03em',
      background: bg,
      border: `1px solid ${border}`,
      color: color
    };
  };

  const toggleExpandRow = (id, e) => {
    e.stopPropagation(); // Prevent triggering selection click
    if (expandedRowId === id) {
      setExpandedRowId(null);
    } else {
      setExpandedRowId(id);
    }
  };

  const formatJson = (val) => {
    if (!val) return '{}';
    let obj = val;
    if (typeof val === 'string') {
      try {
        obj = JSON.parse(val);
      } catch {
        return val;
      }
    }
    return JSON.stringify(obj, null, 2);
  };

  const wrapperStyle = {
    background: 'rgba(15, 23, 42, 0.65)',
    border: '1px solid rgba(79, 70, 229, 0.28)',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '30px',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
  };

  const tableWrapperStyle = {
    overflowX: 'auto',
    borderRadius: '10px',
    border: '1px solid rgba(79, 70, 229, 0.18)',
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.85rem',
    minWidth: '640px',
  };

  const thStyle = {
    padding: '11px 16px',
    textAlign: 'left',
    background: 'rgba(79, 70, 229, 0.14)',
    color: '#A5B4FC',
    fontWeight: '600',
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    borderBottom: '1px solid rgba(79, 70, 229, 0.22)',
    whiteSpace: 'nowrap',
  };

  const tdStyle = {
    padding: '11px 16px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.045)',
    color: '#CBD5E1',
    whiteSpace: 'nowrap',
  };

  const rowStyle = (rec) => ({
    background: selectedRec && selectedRec.id === rec.id ? 'rgba(79, 70, 229, 0.12)' : 'transparent',
    borderLeft: selectedRec && selectedRec.id === rec.id ? '4px solid var(--primary-color)' : '4px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.18s ease'
  });

  const expandBtnStyle = {
    background: 'none',
    border: 'none',
    color: '#A5B4FC',
    fontSize: '1rem',
    cursor: 'pointer',
    padding: '0 4px',
    outline: 'none'
  };

  if (!recommendations || recommendations.length === 0) {
    return (
      <div style={wrapperStyle}>
        <p style={{ textAlign: 'center', color: 'var(--text-secondary)', margin: '20px 0' }}>
          {t('finops.table_no_recs')}
        </p>
      </div>
    );
  }

  return (
    <div style={wrapperStyle}>
      <div style={tableWrapperStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: '40px' }}></th>
              <th style={thStyle}>{t('finops.table_resource')}</th>
              <th style={thStyle}>{t('finops.table_type')}</th>
              <th style={thStyle}>{t('finops.table_risk')}</th>
              <th style={thStyle}>{t('finops.table_savings')}</th>
            </tr>
          </thead>
          <tbody>
            {recommendations.map((rec) => {
              const isExpanded = expandedRowId === rec.id;
              return (
                <React.Fragment key={rec.id}>
                  {/* Fila Principal */}
                  <tr
                    onClick={() => onSelectRec(rec)}
                    style={rowStyle(rec)}
                    className="hover-row"
                  >
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <button
                        onClick={(e) => toggleExpandRow(rec.id, e)}
                        style={expandBtnStyle}
                        title={t('finops.table_justification')}
                      >
                        {isExpanded ? '▼' : '▶'}
                      </button>
                    </td>
                    <td style={{ ...tdStyle, fontWeight: '600', color: '#FFF', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span>{rec.resource_id}</span>
                      {rec.carbon_reduction_potential_gco2e > 0 && (
                        <span className="inline-flex items-center gap-1 bg-green-950/40 text-green-400 border border-green-500/30 px-2 py-0.5 rounded-full text-[10px] font-bold">
                          🍃 {rec.carbon_reduction_potential_gco2e.toFixed(1)} gCO2e
                        </span>
                      )}
                      {rec.energy_reduction_potential_kwh > 0 && (
                        <span className="inline-flex items-center gap-1 bg-emerald-950/40 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-full text-[10px] font-bold">
                          ⚡ {rec.energy_reduction_potential_kwh.toFixed(2)} kWh
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>{t(`finops.resource_types.${rec.resource_type}`, rec.resource_type)}</td>
                    <td style={tdStyle}>
                      <span style={getRiskBadgeStyle(rec.risk_level)}>
                        {rec.risk_level}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, fontWeight: '700', color: '#34D399' }}>
                      {formatCurrency(rec.savings_estimation_usd, 'USD', i18n.language)}
                    </td>
                  </tr>

                  {/* Fila Expandible de Detalle */}
                  {isExpanded && (
                    <tr style={{ background: 'rgba(0, 0, 0, 0.25)' }}>
                      <td colSpan="5" style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.045)' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                          
                          {/* Justificación */}
                          <div>
                            <h4 style={{ margin: '0 0 6px 0', fontSize: '0.8rem', color: '#A5B4FC', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              {t('finops.table_justification')}
                            </h4>
                            <p style={{ margin: 0, color: '#E2E8F0', lineHeight: '1.4', fontSize: '0.85rem', whiteSpace: 'normal' }}>
                              {rec.justification || 'N/A'}
                            </p>
                          </div>

                          {/* Métricas Metabólicas */}
                          {(rec.carbon_footprint_gco2e > 0 || rec.energy_consumption_kwh > 0) && (
                            <div>
                              <h4 style={{ margin: '0 0 6px 0', fontSize: '0.8rem', color: '#34D399', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                🍃 {t('finops.metabolic_metrics')}
                              </h4>
                              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '4px' }}>
                                <span className="inline-flex items-center gap-1.5 bg-green-950/40 text-green-400 border border-green-500/30 px-3 py-1 rounded-full text-xs font-bold">
                                  {t('finops.carbon_footprint')}: {rec.carbon_footprint_gco2e.toFixed(1)} gCO2e | {t('finops.carbon_reduction_potential')}: {rec.carbon_reduction_potential_gco2e.toFixed(1)} gCO2e
                                </span>
                                <span className="inline-flex items-center gap-1.5 bg-emerald-950/40 text-emerald-400 border border-emerald-500/30 px-3 py-1 rounded-full text-xs font-bold">
                                  {t('finops.energy_consumption')}: {rec.energy_consumption_kwh.toFixed(2)} kWh | {t('finops.chart_saving_estimation')}: {rec.energy_reduction_potential_kwh.toFixed(2)} kWh
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Config Comparación */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                            <div>
                              <h4 style={{ margin: '0 0 6px 0', fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {t('finops.table_config_current')}
                              </h4>
                              <pre style={{
                                background: '#0F172A',
                                border: '1px solid var(--glass-border)',
                                borderRadius: '8px',
                                padding: '10px',
                                color: '#94A3B8',
                                fontSize: '0.75rem',
                                overflowX: 'auto',
                                margin: 0,
                                fontFamily: 'monospace'
                              }}>
                                {formatJson(rec.current_configuration)}
                              </pre>
                            </div>
                            <div>
                              <h4 style={{ margin: '0 0 6px 0', fontSize: '0.8rem', color: '#34D399', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {t('finops.table_config_recommended')}
                              </h4>
                              <pre style={{
                                background: '#0F172A',
                                border: '1px solid rgba(52, 211, 153, 0.3)',
                                borderRadius: '8px',
                                padding: '10px',
                                color: '#A7F3D0',
                                fontSize: '0.75rem',
                                overflowX: 'auto',
                                margin: 0,
                                fontFamily: 'monospace'
                              }}>
                                {formatJson(rec.recommended_configuration)}
                              </pre>
                            </div>
                          </div>

                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <style>{`
        .hover-row:hover {
          background: rgba(255, 255, 255, 0.035) !important;
        }
      `}</style>
    </div>
  );
};

export default RecommendationsTable;
